import {
  Avatar,
  Button,
  Flex,
  Grid,
  Progress,
  Text,
  TextField,
  DropdownMenu,
} from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { Navigate, createRoute } from "@tanstack/react-router";
import { Label } from "radix-ui";
import { useReducer, useState, useEffect } from "react";

import { useUI } from "../../../contexts/ui";
import { getFolderTree } from "../../../services/folders";
import { getTicketVersionStatus } from "../../../services/projects";
import { uploadFile } from "../../../services/upload";
import { useAnalytics } from "../../../utils/analytics";
import { ChromeMessageType } from "../../../utils/chrome-extension-types";
import { getNameInitials } from "../../../utils/constants";
import { isNil } from "../../../utils/fp";
import { useAppSelector } from "../../../utils/hooks";
import { assetForUser } from "../../../utils/imageHandler";
import {
  useSwitchEnterprise,
  useEnterprises,
} from "../../../utils/queries/enterprises";
import {
  useProjectMutation,
  useRevisionMutation,
} from "../../../utils/queries/projects";
import { authLayoutRoute } from "../../auth-layout/route";
import { loginRoute } from "../../login/route";

type Status =
  | "idle"
  | "asset_selected"
  | "asset_uploading"
  | "ticket_ready"
  | "processing"
  | "ai_processing"
  | "ai_running";

type AssetFlowState = {
  status: Status;
  imgBlob?: number[];
  imgMime?: string;
  ticketId?: string;
  title?: string;
};

type AssetFlowAction =
  | {
      type: "asset_selected";
      payload: {
        src: string;
        blob: number[];
        type?: string;
        ticketId?: string;
      };
    }
  | { type: "asset_uploading" }
  | { type: "ticket_ready"; payload: { ticketId: string } }
  | { type: "idle" }
  | { type: "processing" }
  | { type: "ai_processing" }
  | { type: "ai_running" };

const initialAssetFlowState: AssetFlowState = {
  status: "idle",
};

function assetFlowReducer(
  state: AssetFlowState,
  action: AssetFlowAction,
): AssetFlowState {
  switch (action.type) {
    case "asset_selected":
      return {
        ...state,
        status: "asset_selected",
        imgBlob: action.payload.blob,
        imgMime: action.payload.type || "image/png",
        ticketId: action.payload.ticketId,
        title: action.payload.src,
      };
    case "asset_uploading":
      return { ...state, status: "asset_uploading" };
    case "ticket_ready":
      return {
        ...state,
        status: "ticket_ready",
        ticketId: action.payload.ticketId,
      };
    case "idle":
      return { ...initialAssetFlowState };
    case "processing":
      return { ...state, status: "processing" };
    case "ai_processing":
      return { ...state, status: "ai_processing" };
    case "ai_running":
      return { ...state, status: "ai_running" };
    default:
      return state;
  }
}

export const chromeExtensionRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "tickets/chrome-extension",
  component: function Component() {
    const user = useAppSelector((state) => state.auth.value);
    const { notify } = useUI();
    const analytics = useAnalytics("ChromeExtension");
    const enterprisesQuery = useEnterprises();
    const switchEnterpriseMutation = useSwitchEnterprise();
    const userEnterpriseId = useAppSelector(
      (state) => state.auth.value!.enterpriseId,
    );
    const userEmail = useAppSelector((state) => state.auth.value!.email);
    const [flowState, dispatchState] = useReducer(
      assetFlowReducer,
      initialAssetFlowState,
    );
    const [temporaryReviewName, setTemporaryReviewName] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const [folderId, setFolderId] = useState<string>("no_folder");
    const [pageInfo, setPageInfo] = useState<{
      isGoogleDocs: boolean;
      docType: "document" | "presentation" | null;
      title: string;
    }>({
      isGoogleDocs: false,
      docType: null,
      title: "",
    });

    const ticketMutation = useProjectMutation();
    const revisionMutation = useRevisionMutation();
    const folderTree = useQuery({
      queryKey: ["folders", "tree"],
      queryFn: getFolderTree,
    });

    useEffect(() => {
      // Request page info from the extension
      window.parent.postMessage(
        {
          type: ChromeMessageType.GET_PAGE_INFO,
        },
        "*",
      );

      // Listen for page info response
      async function handleMessage(event: MessageEvent) {
        const data = event.data;
        if (!temporaryReviewName) {
          setTemporaryReviewName(data.payload.title);
        }
        if (data.type === ChromeMessageType.PAGE_INFO) {
          setPageInfo(data.payload);
        } else if (data.type === ChromeMessageType.EXPORT_RESULT) {
          if (data.payload.error) {
            setIsExporting(false);
            notify({
              title: "Error",
              message: data.payload.error,
              variant: "error",
            });
            return;
          }

          try {
            // Convert the blob data to a File object
            const uint8arr = new Uint8Array(data.payload.blob);
            const blob = new Blob([uint8arr], { type: "application/pdf" });
            const file = new File([blob], `${data.payload.title}.pdf`, {
              type: "application/pdf",
            });

            // Get presigned URL and upload to S3
            const payload = await uploadFile(file, {}, true);
            const key = payload.key;

            const asset = {
              source: `https://static.puntt.ai/${key}`,
              type: "file" as const,
            };

            // Create the ticket
            const ticket = await ticketMutation.mutateAsync({
              title: data.payload.title,
              folder: folderId === "no_folder" ? undefined : folderId,
            });

            const revision = await revisionMutation.mutateAsync({
              ticketId: ticket._id,
              payload: {
                name: "Version 1",
                documentImportType: file.name
                  .slice(file.name.lastIndexOf(".") + 1)
                  .toUpperCase(),
                reviewFiles: [asset],
              },
            });

            // Set processing state before starting the polling
            dispatchState({
              type: "processing",
            });

            // Poll for version status until it's no longer pending
            let attempts = 0;
            const maxAttempts = 100;
            const pollInterval = setInterval(async () => {
              attempts++;
              const currentStatus = await getTicketVersionStatus({
                ticketId: ticket._id,
                versionId: revision.boards[0]._id,
              });

              if (!currentStatus.isPending || attempts >= maxAttempts) {
                clearInterval(pollInterval);

                dispatchState({
                  type: "ticket_ready",
                  payload: {
                    ticketId: ticket._id,
                  },
                });
                setIsExporting(false);
              }
            }, 2000);
          } catch (_error) {
            setIsExporting(false);
            notify({
              title: "Error",
              message: "Failed to process the document",
              variant: "error",
            });
          }
        }
      }

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [
      temporaryReviewName,
      folderId,
      notify,
      ticketMutation,
      revisionMutation,
    ]);

    async function handleExport() {
      setIsExporting(true);
      window.parent.postMessage(
        {
          type: ChromeMessageType.EXPORT_DOCUMENT,
        },
        "*",
      );
    }

    async function letsGo() {
      if (!flowState.ticketId) {
        notify({
          title: "Error",
          message: "No ticket ID found",
          variant: "error",
        });
        return;
      }

      // Open ticket in new tab
      window.open(`/tickets/${flowState.ticketId}`, "_blank");
    }

    function handleSwitchEnterprise(enterpriseId: string) {
      if (enterpriseId !== userEnterpriseId) {
        const start = performance.now();
        switchEnterpriseMutation.mutate(enterpriseId, {
          onSuccess: () => {
            analytics.capture("switch_enterprise", {
              from: userEnterpriseId,
              to: enterpriseId,
              duration_ms: performance.now() - start,
              user: userEmail,
            });
            location.reload();
          },
        });
      }
    }

    if (!user) {
      return (
        <Navigate
          to={loginRoute.to}
          search={{ redirect: chromeExtensionRoute.to }}
        />
      );
    }
    const renderContent = () => {
      if (!pageInfo.isGoogleDocs) {
        return (
          <Text size="4" align="center">
            Please open a Google Docs or Google Slides document to use this
            extension.
          </Text>
        );
      }

      if (flowState.status === "idle") {
        return (
          <>
            <label htmlFor="ticket-name">
              <Text as="div" size="2" mb="1" weight="bold">
                New Ticket Name
              </Text>
              <TextField.Root
                name="ticket-name"
                value={temporaryReviewName}
                onChange={(e) => setTemporaryReviewName(e.target.value)}
              />
            </label>

            <Label.Root htmlFor="chrome-ticket-folder">
              <Text as="div" size="2" mb="1" weight="bold">
                Select a Folder
              </Text>

              <DropdownMenu.Root>
                <DropdownMenu.Trigger
                  className="min-w-[200px]"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: "32px",
                    border: "1px solid var(--gray-6)",
                    borderRadius: 6,
                    padding: "0 12px",
                    background: "var(--color-panel-solid)",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {(() => {
                        if (folderId === "no_folder") return "No Folder";
                        const findFolder = (folders: any[]): string | null => {
                          for (const f of folders) {
                            if (f._id === folderId) return f.name;
                            if (f.folders) {
                              const found = findFolder(f.folders);
                              if (found) return found;
                            }
                          }
                          return null;
                        };
                        return findFolder(folderTree.data ?? []) || "No Folder";
                      })()}
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="#888"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  align="start"
                  style={{ minWidth: 200, maxWidth: 300 }}
                >
                  <DropdownMenu.Item
                    onSelect={() => setFolderId("no_folder")}
                    style={{
                      fontWeight: folderId === "no_folder" ? "bold" : "normal",
                      background:
                        folderId === "no_folder" ? "var(--gray-3)" : undefined,
                    }}
                  >
                    No Folder
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator />
                  {renderFolderList(
                    (folderTree.data ?? []) as Folder[],
                    folderId,
                    setFolderId,
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Label.Root>

            <Button
              className="w-full"
              onClick={handleExport}
              loading={isExporting}
              disabled={isExporting}
            >
              Upload for Review
            </Button>
          </>
        );
      }

      if (flowState.status === "processing") {
        return (
          <>
            <Text size="3" align="center">
              Uploading...
            </Text>

            <Progress size="2" duration="30s" key="processing-progress" />
          </>
        );
      }
      if (flowState.status === "ai_processing") {
        return (
          <>
            <Text size="4" align="center">
              Preparing your document for AI
            </Text>

            <Progress size="3" duration="90s" key="ai-processing-progress" />
          </>
        );
      }

      if (flowState.status === "ai_running") {
        return (
          <>
            <Text size="4" align="center">
              Your AI Review is in progress, hang tight!
            </Text>

            <Progress size="3" duration="120s" key="ai-running-progress" />
            <Text size="4" align="center">
              See your ticket now and we will keep running your AI review in the
              background.
            </Text>
            <Button onClick={() => letsGo()}>See Ticket</Button>
          </>
        );
      }
      if (flowState.status === "asset_selected") {
        return (
          <>
            <Text size="4" align="center">
              {flowState.title} is ready
            </Text>

            <Button onClick={() => letsGo()}>See Ticket</Button>
          </>
        );
      }

      if (flowState.status === "ticket_ready") {
        return (
          <>
            <Text size="3">Uploaded Successfully</Text>

            <Button onClick={letsGo} className="w-full" size="2">
              Continue in Puntt
            </Button>
          </>
        );
      }

      return null;
    };

    return (
      <Grid
        align="center"
        justify="between"
        p="4"
        gap="5"
        style={{ height: "100%" }}
      >
        <Flex gap="4" align="center">
          {!isNil(user?.email) &&
          enterprisesQuery.data &&
          enterprisesQuery.data.length > 1 ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                className="min-w-[200px]"
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "32px",
                  border: "1px solid var(--gray-6)",
                  borderRadius: 6,
                  padding: "0 12px",
                  background: "var(--color-panel-solid)",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {enterprisesQuery.data.find(
                      (e) => e._id === userEnterpriseId,
                    )?.name || "Select Enterprise"}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="#888"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                align="start"
                style={{
                  minWidth: 200,
                  maxWidth: 300,
                }}
              >
                {enterprisesQuery.data
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((enterprise) => (
                    <DropdownMenu.Item
                      key={enterprise._id}
                      onSelect={() => handleSwitchEnterprise(enterprise._id!)}
                      style={{
                        fontWeight:
                          enterprise._id === userEnterpriseId
                            ? "bold"
                            : "normal",
                        background:
                          enterprise._id === userEnterpriseId
                            ? "var(--gray-3)"
                            : undefined,
                      }}
                    >
                      {enterprise.name}
                    </DropdownMenu.Item>
                  ))}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          ) : (
            <div
              className="w-full min-w-[200px]"
              style={{
                display: "flex",
                alignItems: "center",
                height: "32px",
                padding: "0 12px",
              }}
            >
              <Text>
                {enterprisesQuery.data?.find((e) => e._id === userEnterpriseId)
                  ?.name || "Loading..."}
              </Text>
            </div>
          )}
          <Avatar
            variant="solid"
            size="1"
            src={assetForUser(user.avatar)}
            fallback={getNameInitials(user.name)}
          />
        </Flex>
        {renderContent()}
      </Grid>
    );
  },
});

// Helper function for rendering nested folders as a flat list with indentation
interface Folder {
  _id: string;
  name: string;
  folders?: Folder[];
}
function renderFolderList(
  folders: Folder[],
  folderId: string,
  setFolderId: (id: string) => void,
  level = 0,
): JSX.Element[] {
  return folders.flatMap((folder: Folder) => [
    <DropdownMenu.Item
      key={folder._id}
      onSelect={() => setFolderId(folder._id)}
      style={{
        fontWeight: folder._id === folderId ? "bold" : "normal",
        background: folder._id === folderId ? "var(--gray-3)" : undefined,
        paddingLeft: 16 + level * 16,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 220,
        display: "block",
      }}
    >
      {folder.name}
    </DropdownMenu.Item>,
    ...(folder.folders && folder.folders.length > 0
      ? renderFolderList(folder.folders, folderId, setFolderId, level + 1)
      : []),
  ]);
}
